import type { Editor, NodeEntry } from 'slate';
import { Element } from 'slate';
import { Text } from 'slate';
import { Node } from 'slate';
import { safeJson } from '@/scripts/utils';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  deserializePayloadRender_mention,
  serializePayload_mention,
} from '../chat-editor/components/mention';
import {
  deserializePayloadRender_link,
  serializePayload_link,
} from '../chat-editor/components/link';

export function handleKeyDown(
  e: React.KeyboardEvent<HTMLDivElement>,
  lineBreak: () => void,
  send: () => void
) {
  switch (e.key) {
    case 'Enter':
      e.preventDefault();
      if (e.ctrlKey || e.shiftKey) {
        lineBreak();
      } else {
        send();
      }
      break;
  }
}

export interface PayloadDataEntity {
  type: string;
  offset: number;
  length: number;
  [key: string]: any;
}

export interface PayloadData {
  content: string;
  entities: PayloadDataEntity[];
}

export interface SerializePayloadCallback {
  (value: {
    part?: string;
    entity?: Omit<PayloadDataEntity, 'offset' | 'length'>;
  }): void;
}

export interface SerializePayloadOptions {
  editor: Editor;
  entry: NodeEntry;
  callback: SerializePayloadCallback;
}

export function serializePayload(editor: Editor) {
  const mentionIds: string[] = [];
  const entities: PayloadDataEntity[] = [];

  let content = '';

  const callback: SerializePayloadCallback = ({ part, entity }) => {
    if (part) {
      if (entity) {
        entities.push({
          type: '',
          offset: content.length,
          length: part.length,
          ...entity,
        });
      }
      content += part;
    }
  };

  const gen = Node.nodes(editor);

  let linkCount = 0;

  for (const entry of gen) {
    const [node, path] = entry as NodeEntry<Element>;

    const isText = Text.isText(node);
    const isElement = Element.isElement(node);
    const isVoid = editor.isVoid(node);

    if (isText ? node.text : isElement ? isVoid : false) {
      const baseOptions: SerializePayloadOptions = { editor, entry, callback };

      const has = (
        [[serializePayload_mention, { mentionIds }], [serializePayload_link]] as any[]
      ).some(([handler, options]) => handler({ ...baseOptions, ...options }));

      if (!has) {
        content += Node.string(node);
      }
    }

    if (linkCount < path[0]) {
      content += '\n';
      linkCount = path[0];
    }
  }

  const payload: PayloadData = {
    content,
    entities,
  };

  return {
    payload: payload.content ? JSON.stringify(payload) : '',
    mentionIds,
  };
}

export interface DeserializePayloadRenderOptions {
  type: string;
  key: string;
  text: string;
  entity: PayloadDataEntity;
  msg: ChatMessage;
}

export interface DeserializePayloadRenderResult {
  children: ReactNode;
}

export interface DeserializePayloadRender {
  (props: DeserializePayloadRenderOptions): void | DeserializePayloadRenderResult;
}

export function deserializePayload(msg: ChatMessage) {
  const payloadData = safeJson(msg.payload, {} as PayloadData);
  let payloadChildren: ReactNode = null;
  if (payloadData.content && payloadData.entities) {
    if (payloadData.entities.length) {
      let currentIndex = 0;
      const resultNodes: ReactNode[] = [];
      const content = payloadData.content;

      payloadData.entities.forEach((entity) => {
        const { offset, length, type } = entity;
        const entityEnd = offset + length;

        if (currentIndex < offset) {
          resultNodes.push(content.substring(currentIndex, offset));
        }

        const entityText = content.substring(offset, entityEnd);

        const renders = [
          deserializePayloadRender_mention,
          deserializePayloadRender_link,
        ];

        let children: ReactNode = entityText;

        for (const render of renders) {
          const result = render({
            type,
            entity,
            key: `${offset}-${length}`,
            text: entityText,
            msg,
          });
          if (result) {
            children = result.children;
            break;
          }
        }

        resultNodes.push(children);

        currentIndex = entityEnd;
      });

      if (currentIndex < content.length) {
        resultNodes.push(content.substring(currentIndex));
      }

      payloadChildren = resultNodes;
    } else {
      payloadChildren = payloadData.content;
    }
  } else {
    payloadChildren = msg.payload;
  }

  return payloadChildren;
}

export function deserializePayloadContent(payload: string) {
  const payloadData = safeJson(payload, {} as PayloadData);
  let content = '';
  if (payloadData.content && payloadData.entities) {
    content = payloadData.content;
  } else {
    content = payload;
  }

  return content;
}
